import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Form from "react-validation/build/form";
import { login, getUserCompanySeller } from "../../store/actions/auth";
import "../../components/login/bootstraplogin.css";
import MainLogoV from "../../assets/images/main-logo-vertical.png"
import MainLogoAi from "../../assets/images/bannerLogin.svg"
import EmailIcon from "../../assets/icons/email-icon.png"
import PasswordIcon from "../../assets/icons/password-icon.png"
import { findIsHostShipAi } from "../../config/utils";
import { initiateWalmartOAuthWithParams, hasSellerContext } from "../../services/walmart.service";
import { setLocalStorageItem } from "../../services/StorageService";
import PropTypes from "prop-types";

const Login = (props) => {
  const form = useRef();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { isLoggedIn, companyList, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const companyFetchDispatched = useRef(false);
  const [walmartConnectTimedOut, setWalmartConnectTimedOut] = useState(false);

  // Check for Walmart OAuth params on mount
  useEffect(() => {
    const walmartCallbackUri = searchParams.get('walmartCallbackUri');
    const clientType = searchParams.get('clientType');
    
    // If Walmart params are present, store them for after login
    if (walmartCallbackUri) {
      sessionStorage.setItem('walmart_callback_uri', walmartCallbackUri);
      sessionStorage.setItem('walmart_client_type', clientType || 'seller');
      sessionStorage.setItem('walmart_pending_connect', 'true');
    }
  }, [searchParams]);

  // Fetch company list when logged in with pending Walmart connect
  useEffect(() => {
    const pendingConnect = sessionStorage.getItem('walmart_pending_connect') === 'true'
      || searchParams.has('walmartCallbackUri');
    if (isLoggedIn && pendingConnect && !companyFetchDispatched.current) {
      companyFetchDispatched.current = true;
      dispatch(getUserCompanySeller());

      // Safety timeout: if companyList never loads, abort after 30s
      const timer = setTimeout(() => {
        setWalmartConnectTimedOut(true);
        sessionStorage.removeItem('walmart_pending_connect');
        sessionStorage.removeItem('walmart_callback_uri');
        sessionStorage.removeItem('walmart_client_type');
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn]);

  // Handle Walmart OAuth redirect when companyList is loaded
  useEffect(() => {
    const pendingConnect = sessionStorage.getItem('walmart_pending_connect') === 'true';
    if (!isLoggedIn || !pendingConnect || !companyList?.length) return;

    if (companyList.length === 1) {
      setLocalStorageItem('currentCompany', JSON.stringify(companyList[0]));
      proceedToWalmartOAuth();
    } else if (hasSellerContext()) {
      proceedToWalmartOAuth();
    } else {
      navigate('/walmart/select-seller');
    }
  }, [isLoggedIn, companyList]);

  const onChangeUsername = (e) => {
    const usernameValue = e.target.value;
    setUsername(usernameValue);
  };

  const handleBlur = (e) => {
    setUsername(e.target.value.trim());
  }

  const onChangePassword = (e) => {
    const passwordValue = e.target.value;
    setPassword(passwordValue);
  };

  const proceedToWalmartOAuth = () => {
    const walmartCallbackUri = sessionStorage.getItem('walmart_callback_uri');
    const clientType = sessionStorage.getItem('walmart_client_type') || 'seller';
    
    // Clear the pending connect flag
    sessionStorage.removeItem('walmart_pending_connect');
    sessionStorage.removeItem('walmart_callback_uri');
    sessionStorage.removeItem('walmart_client_type');
    
    // Redirect to Walmart OAuth
    initiateWalmartOAuthWithParams(walmartCallbackUri, clientType);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    form.current.validateAll();
    dispatch(login(username, password))
      .then((res) => {
        if (res?.Value?.roleName === "Onboarding Seller") {
          navigate('/user-onboarding');
          return;
        }
        // Login succeeded — check if there's a pending Walmart connection
        const pendingWalmartConnect = sessionStorage.getItem('walmart_pending_connect');

        if (pendingWalmartConnect === 'true') {
          // Fetch company/seller list - the useEffect will handle the rest
          companyFetchDispatched.current = true;
          dispatch(getUserCompanySeller());
          // Keep loading state while we wait for companyList
        } else {
          navigate("/profile");
          window.location.reload();
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  if (isLoggedIn) {
    if (user?.Value?.roleName === "Onboarding Seller") {
      return <Navigate to="/user-onboarding" />;
    }

    // Check both sessionStorage AND URL params — on first render the useEffect
    // that stores params in sessionStorage hasn't run yet, so we must also
    // check the URL directly to avoid redirecting away prematurely.
    const pendingWalmartConnect = sessionStorage.getItem('walmart_pending_connect') === 'true'
      || searchParams.has('walmartCallbackUri');
    if (pendingWalmartConnect && !walmartConnectTimedOut) {
      return (
        <div className="cus-login-pg login-form-center">
          <div className="text-center">
            <span className="spinner-border spinner-border-lg"></span>
            <p className="mt-3">Connecting to Walmart...</p>
          </div>
        </div>
      );
    }
    return <Navigate to="/" />;
  }

  return (
    <div className="cus-login-pg login-form-center">
      <div>
        <div className="cus-login-pg-sec">
          <div className="cus-login-pg-left-sec">
            <img alt="" className="cus-main-logo-img" src={findIsHostShipAi() ? MainLogoAi : MainLogoV} />
          </div>
          <div className="cus-login-pg-right-sec">
            <div className="cus-welcome-txt">Welcome to <span>{findIsHostShipAi() ? 'ShipVisionAI' : '3PLVision'}</span></div>
            <div className="cus-login-form">
              <div className="cus-form-sec-heading">Log In</div>
              <div className="cus-login-form-sec">
                <Form className='loginFormSubmit' onSubmit={handleLogin} ref={form}>
                  <div className="form-group cus-rel fontFix">
                    <div className="cus-rel">
                      <input
                        type="text"
                        className="form-controlzm loginInput"
                        autoComplete={'false'}
                        name="username"
                        value={username}
                        onChange={onChangeUsername}
                        placeholder="Enter your email address"
                        onBlur={handleBlur}
                      />
                      <img alt="" className="cus-sm-icon-cls" src={EmailIcon} />
                    </div>
                    <div className="cus-rel">
                      <input
                        type="password"
                        className="form-controlzm loginInput"
                        name="password"
                        value={password}
                        onChange={onChangePassword}
                        autoComplete='off'
                        placeholder="Enter your Password"
                      />
                      <img alt="" className="cus-sm-icon-cls" src={PasswordIcon} />
                    </div>
                  </div>

                  <div className="form-group cus-action-group fontFix">
                    <div className="cus-action-btn-sec">
                      <button
                        className="btn cus-seconday-bg-btn loginBtn"
                        disabled={!password || !username || loading}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <>Login</>
                        )}
                      </button>
                    </div>
                    <div className="form-group forgot-password">
                      <Link to={'/forgotPassword'}>Forgot Password</Link>
                    </div>
                  </div>
                  <div className="sgnUptxt mt-3 fontFix">Don't have account? <Link to={'/user-signup'}>Sign Up</Link></div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Login.propTypes = {
  history: PropTypes.object,
};

export default Login;